<template>
  <div class="nd-page prescription-details-page">
    <v-card class="nd-section-card prescription-form-card">
      <div class="prescription-card-header">
        <div>
          <h2>Dados da prescrição</h2>
          <p>Selecione o paciente, registre observações e organize os medicamentos prescritos.</p>
        </div>
        <v-chip v-if="prescription.id" class="prescription-id-chip" small label>
          Prescrição #{{ prescription.id }}
        </v-chip>
      </div>

      <section class="prescription-form-section">
        <div class="prescription-info-grid">
          <div class="prescription-field-block">
            <div class="prescription-section-title">
              <v-icon color="primary" size="18">mdi-account-outline</v-icon>
              <div>
                <h3>Paciente</h3>
                <p>Busque pelo paciente vinculado a esta orientação.</p>
              </div>
            </div>
            <v-autocomplete
              v-model="prescription.patientId"
              :items="patientItems"
              :loading="loadingPatients"
              :search-input.sync="patientSearch"
              item-text="name"
              item-value="id"
              label="Selecionar paciente"
              outlined
              dense
              clearable hide-details="auto"
              no-data-text="Nenhum paciente encontrado"
              placeholder="Digite para buscar..."
              prepend-inner-icon="mdi-magnify"
              @change="onPatientChange"
            ></v-autocomplete>

            <!-- Allergy warning -->
            <div v-if="loadingAllergies" class="mt-3 d-flex align-center">
              <v-progress-circular indeterminate size="16" width="2" color="warning" class="mr-2"></v-progress-circular>
              <span class="caption grey--text">Verificando alergias...</span>
            </div>

            <v-alert
              v-else-if="patientAllergies.length > 0"
              type="warning"
              dense
              outlined
              class="mt-3 mb-0 allergy-alert"
              icon="mdi-alert-outline"
            >
              <div class="font-weight-bold mb-1" style="font-size:13px;">
                {{ patientAllergies.length }} alergia{{ patientAllergies.length > 1 ? 's' : '' }} registrada{{ patientAllergies.length > 1 ? 's' : '' }}
              </div>
              <div
                v-for="(a, i) in patientAllergies"
                :key="i"
                class="mb-1"
                style="font-size:12px; line-height:1.5;"
              >
                <span class="font-weight-medium">{{ a.name }}</span>
                <span v-if="a.medicines && a.medicines.length" class="grey--text text--darken-1">
                  — {{ a.medicines.join(', ') }}
                </span>
                <div v-if="a.symptoms && a.symptoms.length" class="grey--text text--darken-1" style="font-size:11px;">
                  Sintomas: {{ a.symptoms.join(', ') }}
                </div>
              </div>
            </v-alert>

            <div v-else-if="prescription.patientId && !loadingAllergies" class="mt-3">
              <span class="caption success--text">
                <v-icon x-small color="success">mdi-check-circle-outline</v-icon>
                Nenhuma alergia registrada
              </span>
            </div>
          </div>

          <div class="prescription-field-block">
            <div class="prescription-section-title">
              <v-icon color="primary" size="18">mdi-note-text-outline</v-icon>
              <div>
                <h3>Observações</h3>
                <p>Inclua restrições, recomendações e orientações complementares.</p>
              </div>
            </div>
            <v-textarea
              v-model="prescription.notes"
              outlined
              dense hide-details="auto"
              rows="4"
              auto-grow
              placeholder="Observações clínicas, recomendações pós-procedimento, restrições alimentares..."
            ></v-textarea>
          </div>

          <div v-if="prescription.id" class="prescription-field-block prescription-info-panel">
            <div class="prescription-section-title">
              <v-icon color="primary" size="18">mdi-information-outline</v-icon>
              <div>
                <h3>Informações</h3>
                <p>Resumo administrativo da prescrição.</p>
              </div>
            </div>
            <div class="info-row">
              <span class="info-label">Número</span>
              <span class="info-value font-weight-bold">#{{ prescription.id }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Data</span>
              <span class="info-value">{{ formatDate(prescription.date) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Medicamentos</span>
              <span class="info-value">{{ prescription.items.length }} item(s)</span>
            </div>
          </div>
        </div>
      </section>

      <section class="prescription-form-section prescription-medications-section">
        <div class="prescription-medications-header">
          <div class="prescription-section-title">
            <v-icon color="primary" size="18">mdi-pill</v-icon>
            <div>
              <h3>Medicamentos prescritos</h3>
              <p>Adicione dosagem, quantidade, via e posologia de cada item.</p>
            </div>
          </div>
          <v-btn color="primary" elevation="0" @click="addItem">
            <v-icon left small>mdi-plus</v-icon>
            Adicionar
          </v-btn>
        </div>

            <!-- Empty state -->
            <div v-if="prescription.items.length === 0" class="empty-state">
              <v-icon size="48" color="grey lighten-1">mdi-pill-off</v-icon>
              <div class="mt-2 grey--text text--lighten-1 body-2">Nenhum medicamento adicionado</div>
              <v-btn text small color="primary" class="mt-2" @click="addItem">
                <v-icon left small>mdi-plus</v-icon> Adicionar medicamento
              </v-btn>
            </div>

            <!-- Medication items -->
            <div
              v-for="(item, index) in prescription.items"
              :key="index"
              class="med-item mb-4"
            >
              <!-- Item header -->
              <div class="med-item-header d-flex align-center px-3 py-2">
                <div class="med-badge mr-2">{{ index + 1 }}</div>
                <span class="font-weight-medium" style="font-size:13px; color:#3b3b3b;">
                  {{ item.medicationName || `Medicamento ${index + 1}` }}
                </span>
                <v-spacer></v-spacer>
                <v-btn icon x-small @click="removeItem(index)" color="error">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>

              <!-- Item fields -->
              <div class="med-item-body pa-3">
                <v-row dense>
                  <v-col cols="12">
                    <v-text-field
                      v-model="item.medicationName"
                      label="Nome do medicamento"
                      outlined
                      dense hide-details="auto"
                      placeholder="Ex: Amoxicilina 500mg"
                      prepend-inner-icon="mdi-pill"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row dense class="mt-2">
                  <v-col cols="6" sm="4">
                    <v-text-field
                      v-model="item.dosage"
                      label="Dosagem"
                      outlined
                      dense hide-details="auto"
                      placeholder="Ex: 500mg"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6" sm="4">
                    <v-text-field
                      v-model="item.quantity"
                      label="Quantidade"
                      outlined
                      dense hide-details="auto"
                      placeholder="Ex: 1 caixa"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model="item.instructions"
                      label="Via / Forma"
                      outlined
                      dense hide-details="auto"
                      placeholder="Ex: Oral"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row dense class="mt-2">
                  <v-col cols="12">
                    <v-text-field
                      v-model="item.posology"
                      label="Posologia"
                      outlined
                      dense hide-details="auto"
                      placeholder="Ex: Tomar 1 comprimido a cada 8 horas durante 7 dias"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </div>
            </div>
      </section>

      <div class="nd-form-actions prescription-form-actions">
        <div class="prescription-secondary-actions">
          <v-btn v-if="prescription.id" color="primary" outlined elevation="0" @click="printPrescription">
            <v-icon left small>mdi-printer-outline</v-icon>
            Imprimir
          </v-btn>
          <v-btn v-if="prescription.id" color="success" outlined elevation="0" @click="shareWhatsApp">
            <v-icon left small>mdi-whatsapp</v-icon>
            WhatsApp
          </v-btn>
          <v-btn v-if="prescription.id" color="primary" outlined elevation="0" @click="emailDialog = true">
            <v-icon left small>mdi-email-outline</v-icon>
            E-mail
          </v-btn>
        </div>
        <div class="prescription-primary-actions">
          <v-btn color="primary" outlined elevation="0" @click="$router.push('/prescricoes')">
            Voltar
          </v-btn>
          <v-btn color="primary" elevation="0" :loading="loadingSave" @click="savePrescription">
            Salvar
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- Email dialog -->
    <v-dialog v-model="emailDialog" max-width="420" @keydown.esc="emailDialog = false">
      <v-card style="border-radius:16px;">
        <v-card-title class="d-flex align-center pt-5 px-6">
          <v-icon left color="primary">mdi-email-outline</v-icon>
          Enviar por e-mail
        </v-card-title>
        <v-card-text class="px-6 pb-2">
          <div class="caption grey--text mb-4">
            A prescrição será enviada em formato HTML para o endereço informado.
          </div>
          <v-text-field
            v-model="emailTo"
            label="Endereço de e-mail"
            outlined
            dense
            type="email"
            prepend-inner-icon="mdi-at" hide-details="auto"
            autofocus
            @keydown.enter="sendEmail"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer></v-spacer>
          <v-btn text @click="emailDialog = false" :disabled="loadingEmail">Cancelar</v-btn>
          <v-btn color="primary" elevation="0" :loading="loadingEmail" @click="sendEmail">
            <v-icon left small>mdi-send</v-icon>
            Enviar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay :value="loadingPage">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- Print-only layout (hidden on screen, visible only during window.print()) -->
    <div id="print-area">
      <!-- Header -->
      <div class="print-header">
        <div>
          <div class="print-clinic-name">NewDental</div>
          <div class="print-clinic-sub">Clínica Odontológica</div>
        </div>
        <div style="text-align:right;">
          <div class="print-rx-title">PRESCRIÇÃO MÉDICA</div>
          <div v-if="prescription.id" class="print-meta">Nº {{ prescription.id }}</div>
          <div class="print-meta">Data: {{ formatDate(prescription.date || new Date().toISOString()) }}</div>
        </div>
      </div>

      <!-- Patient -->
      <div class="print-patient">
        <span class="print-patient-label">Paciente: </span>
        <span class="print-patient-name">{{ (patientItems.find(x => x.id === prescription.patientId) || {}).name || 'Não informado' }}</span>
      </div>

      <hr class="print-divider" />

      <!-- Rx label -->
      <div class="print-rx-badge">Rx</div>

      <!-- Medications -->
      <div
        v-for="(item, index) in prescription.items"
        :key="index"
        class="print-med-item"
      >
        <div class="print-med-header">
          <span class="print-med-num">{{ index + 1 }}</span>
          <span class="print-med-name">{{ item.medicationName || 'Medicamento' }}</span>
        </div>
        <div v-if="item.dosage || item.quantity" class="print-chips">
          <span v-if="item.dosage" class="print-chip"><b>Dosagem:</b> {{ item.dosage }}</span>
          <span v-if="item.quantity" class="print-chip"><b>Quantidade:</b> {{ item.quantity }}</span>
        </div>
        <div v-if="item.instructions" class="print-instructions">
          <b>Via:</b> {{ item.instructions }}
        </div>
        <div v-if="item.posology" class="print-instructions">
          <b>Posologia:</b> {{ item.posology }}
        </div>
      </div>

      <!-- Notes -->
      <div v-if="prescription.notes" class="print-notes-section">
        <hr class="print-divider" />
        <div class="print-notes-title">Observações</div>
        <div class="print-notes-body">{{ prescription.notes }}</div>
      </div>

      <!-- Footer / signature -->
      <div class="print-footer">
        <hr class="print-divider" />
        <div class="print-sig-line"></div>
        <div class="print-sig-label">Assinatura / Carimbo do Profissional</div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "PrescriptionsDetails",

  data: () => ({
    prescription: {
      id: null,
      date: null,
      patientId: null,
      notes: "",
      items: [],
    },

    patientItems: [],
    patientSearch: "",
    loadingPatients: false,
    patientSearchTimeout: null,

    patientAllergies: [],
    loadingAllergies: false,

    loadingPage: false,
    loadingSave: false,

    emailDialog: false,
    emailTo: "",
    loadingEmail: false,
  }),

  computed: {
    pageTitle() {
      return this.prescription.id ? "Detalhes da Prescrição" : "Nova Prescrição";
    },
    breadcrumbs() {
      return [
        { text: "Homepage", disabled: false, to: "/home", exact: true },
        { text: "Prescrições", disabled: false, to: "/prescricoes", exact: true },
        { text: this.prescription.id ? "Detalhes da Prescrição" : "Nova Prescrição", disabled: true },
      ];
    },
  },

  watch: {
    patientSearch(val) {
      if (!val || val.length < 2) return;
      clearTimeout(this.patientSearchTimeout);
      this.patientSearchTimeout = setTimeout(() => this.searchPatients(val), 300);
    },
  },

  async created() {
    this.loadingPage = true;
    const id = this.$route.params.id;
    if (id) {
      await this.loadPrescription(id);
    }
    this.loadingPage = false;
  },

  methods: {
    async searchPatients(search) {
      this.loadingPatients = true;
      try {
        const result = await axios.get("/patients", { params: { start: 0, limit: 20, search } });
        this.patientItems = result.data.data || [];
      } catch (_) {
        this.patientItems = [];
      } finally {
        this.loadingPatients = false;
      }
    },

    async onPatientChange(patientId) {
      this.patientAllergies = [];
      if (!patientId) return;
      await this.loadPatientAllergies(patientId);
    },

    async loadPatientAllergies(patientId) {
      this.loadingAllergies = true;
      try {
        const result = await axios.get("/allergies", { params: { start: 0, limit: 100 } });
        const all = result.data.data || [];
        this.patientAllergies = all.filter(
          (a) => a.patient && a.patient.id === patientId
        );
      } catch (_) {
        this.patientAllergies = [];
      } finally {
        this.loadingAllergies = false;
      }
    },

    async loadPrescription(id) {
      try {
        const response = await axios.get(`/prescriptions/${id}`);
        const d = response.data.data;
        this.prescription = {
          id: d.id,
          date: d.date || null,
          patientId: d.patient ? d.patient.id : null,
          notes: d.notes || "",
          items: (d.items || []).map((item) => ({
            medicationName: item.medicationName || "",
            dosage: item.dosage || "",
            quantity: item.quantity || "",
            instructions: item.instructions || "",
            posology: item.posology || "",
          })),
        };
        if (d.patient) {
          this.patientItems = [{ id: d.patient.id, name: d.patient.name }];
          await this.loadPatientAllergies(d.patient.id);
        }
      } catch (_) {
        this.$dialog.notify.error("Erro ao carregar prescrição.", { position: "top-right", timeout: 5000 });
      }
    },

    addItem() {
      this.prescription.items.push({
        medicationName: "",
        dosage: "",
        quantity: "",
        instructions: "",
        posology: "",
      });
    },

    removeItem(index) {
      this.prescription.items.splice(index, 1);
    },

    formatDate(date) {
      if (!date) return "—";
      return new Date(date).toLocaleDateString("pt-BR");
    },

    printPrescription() {
      window.print();
    },

    shareWhatsApp() {
      const p = this.prescription;
      const patientName = this.patientItems.find(x => x.id === p.patientId)?.name || 'Paciente';
      const date = this.formatDate(p.date || new Date().toISOString());

      let msg = `*Prescrição NewDental*\n`;
      msg += `Paciente: ${patientName}\n`;
      msg += `Data: ${date}\n\n`;
      msg += `*Medicamentos:*\n`;
      p.items.forEach((item, i) => {
        msg += `\n${i + 1}. *${item.medicationName || 'Medicamento'}*\n`;
        if (item.dosage) msg += `   Dosagem: ${item.dosage}\n`;
        if (item.quantity) msg += `   Quantidade: ${item.quantity}\n`;
        if (item.instructions) msg += `   Via: ${item.instructions}\n`;
        if (item.posology) msg += `   Posologia: ${item.posology}\n`;
      });
      if (p.notes) msg += `\n*Observações:* ${p.notes}`;

      const encoded = encodeURIComponent(msg);
      window.open(`https://wa.me/?text=${encoded}`, '_blank');
    },

    async sendEmail() {
      const email = (this.emailTo || "").trim();
      if (!email || !email.includes("@")) {
        this.$dialog.notify.warning("Informe um endereço de e-mail válido.", { position: "top-right", timeout: 3000 });
        return;
      }
      this.loadingEmail = true;
      try {
        await axios.post(`/prescriptions/${this.prescription.id}/email`, { to: email });
        this.$dialog.notify.success("E-mail enviado com sucesso!", { position: "top-right", timeout: 3000 });
        this.emailDialog = false;
        this.emailTo = "";
      } catch (_) {
        this.$dialog.notify.error("Erro ao enviar e-mail.", { position: "top-right", timeout: 5000 });
      } finally {
        this.loadingEmail = false;
      }
    },

    async savePrescription() {
      if (!this.prescription.patientId) {
        this.$dialog.notify.warning("Selecione um paciente antes de salvar.", { position: "top-right", timeout: 3000 });
        return;
      }
      if (this.prescription.items.length === 0) {
        this.$dialog.notify.warning("Adicione ao menos um medicamento.", { position: "top-right", timeout: 3000 });
        return;
      }

      this.loadingSave = true;
      try {
        const payload = {
          patientId: this.prescription.patientId,
          notes: this.prescription.notes || undefined,
          items: this.prescription.items,
        };

        if (this.prescription.id) {
          await axios.patch(`/prescriptions/${this.prescription.id}`, {
            operation: "update",
            ...payload,
          });
          this.$dialog.notify.success("Prescrição atualizada com sucesso!", { position: "top-right", timeout: 3000 });
        } else {
          const response = await axios.post("/prescriptions", payload);
          this.$dialog.notify.success("Prescrição criada com sucesso!", { position: "top-right", timeout: 3000 });
          this.$router.replace(`/prescricoes/${response.data.data.id}`);
        }
      } catch (_) {
        this.$dialog.notify.error("Erro ao salvar prescrição.", { position: "top-right", timeout: 5000 });
      } finally {
        this.loadingSave = false;
      }
    },
  },
};
</script>

<style scoped>
.prescription-details-page {
  gap: 22px;
}

.prescription-form-card {
  padding: 0;
  overflow: hidden;
}

.prescription-card-header {
  align-items: flex-start;
  border-bottom: 1px solid #eef0ed;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 28px 32px 20px;
}

.prescription-card-header h2,
.prescription-section-title h3 {
  color: #17201c;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0;
}

.prescription-card-header h2 {
  font-size: 22px;
  line-height: 1.15;
}

.prescription-card-header p,
.prescription-section-title p {
  color: #7b8580;
  line-height: 1.35;
  margin: 8px 0 0;
}

.prescription-card-header p {
  font-size: 15px;
}

.prescription-id-chip {
  background: #f1e8fb !important;
  color: #8f4bd0 !important;
  font-weight: 800;
}

.prescription-form-section {
  padding: 24px 32px;
}

.prescription-form-section + .prescription-form-section {
  border-top: 1px solid #eef0ed;
}

.prescription-info-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(280px, 0.9fr) minmax(360px, 1.4fr);
}

.prescription-info-panel {
  grid-column: 1 / -1;
}

.prescription-field-block {
  background: #f8f9f7;
  border: 1px solid #edf0ec;
  border-radius: 8px;
  padding: 20px;
}

.prescription-section-title {
  align-items: flex-start;
  display: flex;
  gap: 10px;
  margin-bottom: 18px;
}

.prescription-section-title h3 {
  font-size: 18px;
  line-height: 1.2;
}

.prescription-section-title p {
  font-size: 14px;
}

.prescription-medications-section {
  padding-bottom: 28px;
}

.prescription-medications-header {
  align-items: flex-start;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  margin-bottom: 18px;
}

.prescription-form-actions {
  border-top: 1px solid #eef0ed;
  justify-content: space-between;
  margin: 0;
  padding: 20px 32px 28px;
}

.prescription-secondary-actions,
.prescription-primary-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.section-card {
  border-radius: 12px !important;
  border-color: #e8e8e8 !important;
}

.section-title {
  font-size: 13px !important;
  font-weight: 700 !important;
  color: #3b3b3b;
  padding: 12px 16px !important;
  letter-spacing: 0.3px;
}

.allergy-alert {
  border-radius: 8px !important;
  font-size: 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #f5f5f5;
}
.info-row:last-child {
  border-bottom: none;
}
.info-label {
  font-size: 12px;
  color: #9e9e9e;
}
.info-value {
  font-size: 13px;
  color: #3b3b3b;
}

.med-item {
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  overflow: hidden;
  background: #ffffff;
}
.med-item-header {
  background-color: #f8f6ff;
  border-bottom: 1px solid #ede9fe;
}
.med-item-body {
  background-color: #ffffff;
}
.med-badge {
  width: 22px;
  height: 22px;
  background-color: #7c3aed;
  color: white;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}

.empty-state {
  background: #f8f9f7;
  border: 1px dashed #dce3df;
  border-radius: 8px;
  text-align: center;
  padding: 40px 20px;
}

@media (max-width: 1120px) {
  .prescription-info-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 780px) {
  .prescription-card-header,
  .prescription-form-section,
  .prescription-form-actions {
    padding-left: 20px;
    padding-right: 20px;
  }

  .prescription-medications-header,
  .prescription-form-actions {
    flex-direction: column;
  }

  .prescription-primary-actions,
  .prescription-secondary-actions {
    width: 100%;
  }
}

/* ── Print area: hidden on screen ── */
#print-area {
  display: none;
}

/* ── When printing: show only the print area ── */
@media print {
  body > * {
    display: none !important;
  }
  /* Vuetify mounts the app under #app */
  #app {
    display: block !important;
  }
  #app > * {
    display: none !important;
  }
  /* The component root is inside v-container → target print-area directly */
  #print-area {
    display: block !important;
    font-family: Arial, sans-serif;
    color: #1e1b2e;
    padding: 24px 32px;
    max-width: 700px;
    margin: 0 auto;
  }

  .print-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    background: #ede9fe;
    border-radius: 8px;
    padding: 14px 18px;
    margin-bottom: 18px;
  }
  .print-clinic-name {
    font-size: 18px;
    font-weight: 700;
    color: #7c3aed;
  }
  .print-clinic-sub {
    font-size: 10px;
    color: #9ca3af;
    margin-top: 2px;
  }
  .print-rx-title {
    font-size: 11px;
    font-weight: 700;
    color: #7c3aed;
  }
  .print-meta {
    font-size: 10px;
    color: #9ca3af;
    margin-top: 3px;
  }

  .print-patient {
    margin-bottom: 14px;
    font-size: 13px;
  }
  .print-patient-label {
    color: #9ca3af;
    font-weight: 600;
  }
  .print-patient-name {
    font-size: 14px;
    font-weight: 700;
    color: #1e1b2e;
  }

  .print-divider {
    border: none;
    border-top: 1px solid #e5e7eb;
    margin: 12px 0;
  }

  .print-rx-badge {
    display: inline-block;
    background: #7c3aed;
    color: white;
    font-size: 14px;
    font-weight: 700;
    padding: 4px 12px;
    border-radius: 6px;
    margin-bottom: 12px;
  }

  .print-med-item {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 10px 14px;
    margin-bottom: 10px;
  }
  .print-med-header {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
  }
  .print-med-num {
    background: #7c3aed;
    color: white;
    border-radius: 4px;
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    font-weight: 700;
    margin-right: 8px;
    flex-shrink: 0;
  }
  .print-med-name {
    font-size: 12px;
    font-weight: 700;
    color: #1e1b2e;
  }
  .print-chips {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 4px;
  }
  .print-chip {
    background: #ede9fe;
    color: #1e1b2e;
    font-size: 9px;
    padding: 2px 8px;
    border-radius: 20px;
  }
  .print-chip b {
    color: #7c3aed;
  }
  .print-instructions {
    font-size: 10px;
    color: #1e1b2e;
    margin-top: 2px;
  }

  .print-notes-section {
    margin-top: 4px;
  }
  .print-notes-title {
    font-size: 11px;
    font-weight: 700;
    color: #1e1b2e;
    margin-bottom: 4px;
  }
  .print-notes-body {
    font-size: 10px;
    color: #1e1b2e;
  }

  .print-footer {
    margin-top: 40px;
  }
  .print-sig-line {
    width: 200px;
    height: 1px;
    background: #1e1b2e;
    margin-top: 40px;
    margin-bottom: 4px;
  }
  .print-sig-label {
    font-size: 9px;
    color: #9ca3af;
  }
}
</style>
